import Badge, { ColorType } from '@/app/common/components/Badge'
import { ACETable } from '@/app/common/components/Table/ACETable'
import { useAceTable } from '@/app/common/components/Table/useAceTable'
import { ProviderAffiliation } from '@/queries/providers/affiliations/schema.ts'
import { createColumnHelper } from '@tanstack/react-table'
import { useState } from 'react'
import SimpleSelector from '@/common/components/SimpleSelector'
import usePermissionAlert from '@/hook/usePermissionAlert'
import { useUserPrivileges } from '@/hook/useUserPrivileges'
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@/common/components/Menu.tsx'
import {
  EditIconGeneral,
  FilterIcon,
  KebabMenuIcon,
  UserCheckIcon,
  TrashCanIcon,
} from '@/common/icons/general.tsx'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { useNavigate } from 'react-router-dom'
import AddAffiliationPopup from '@/pages/setup/provider_affiliations/_components/AddAffiliationPopup.tsx'
import EditAffiliationPopup from '@/pages/setup/provider_affiliations/_components/EditAffiliationPopup.tsx'
import { ConfirmDialog } from '@/app/common/components/confirm-dialog/ConfirmDialog'
import { useDeleteProviderAffiliation } from '@/queries/providers/affiliations/hooks.ts'
import { useToast } from '@/shad/ui/toast/use-toast.ts'

type Props = {
  affiliations: ProviderAffiliation[]
  onRefresh: () => void
}

type StatusOption = {
  id: number
  name: 'All' | 'Active' | 'Inactive'
}

const STATUS_OPTIONS: StatusOption[] = [
  { id: 0, name: 'All' },
  { id: 1, name: 'Active' },
  { id: 2, name: 'Inactive' },
]

const columnHelper = createColumnHelper<ProviderAffiliation>()

function getStatusColor(status: ProviderAffiliation['status']): ColorType {
  return status === 'Active' ? 'Positive' : 'Negative'
}

type AffiliationsTableColumnActions = {
  canEditAffiliation: boolean
  canManageProviders: boolean
  canDeleteAffiliation: boolean
  navigate: ReturnType<typeof useNavigate>
  showPermissionDeniedMessage: () => void | Promise<void>
  onEditAffiliation: (affiliation: ProviderAffiliation) => void
  onDeleteAffiliation: (affiliation: ProviderAffiliation) => void
}

const createAffiliationsColumns = ({
  canEditAffiliation,
  canManageProviders,
  canDeleteAffiliation,
  navigate,
  showPermissionDeniedMessage,
  onEditAffiliation,
  onDeleteAffiliation,
}: AffiliationsTableColumnActions) => [
  columnHelper.accessor('id', {
    header: 'ID',
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => (
      <div className="max-w-sm truncate" title={info.getValue()}>
        {info.getValue()}
      </div>
    ),
  }),
  columnHelper.accessor('connected_providers_count', {
    header: () => <div className="w-full text-center">Connected Providers</div>,
    cell: (info) => (
      <div className="w-full text-center">{info.getValue() ?? 0}</div>
    ),
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => (
      <Badge semanticColor={getStatusColor(info.getValue())}>
        {info.getValue()}
      </Badge>
    ),
  }),
  columnHelper.display({
    id: 'action',
    header: () => <div className="w-full text-center">Action</div>,
    cell: (info) => {
      const row = info.row.original

      return (
        <div className="w-full flex justify-center">
          <Menu>
            <MenuButton aria-label="Open affiliation actions">
              <KebabMenuIcon />
            </MenuButton>
            <MenuItems className="w-44 bottom-8 right-auto left-1/2 -translate-x-1/2 origin-bottom">
              <MenuItem
                className="group flex justify-start items-center gap-2"
                onClick={() => {
                  if (!canEditAffiliation) {
                    void showPermissionDeniedMessage()
                    return
                  }

                  onEditAffiliation(row)
                }}
              >
                <EditIconGeneral className="text-lg text-gray-400 group-hover:text-primary" />
                <span>Edit</span>
              </MenuItem>

              <MenuItem
                className="group flex justify-start items-center gap-2"
                onClick={() => {
                  if (!canManageProviders) {
                    void showPermissionDeniedMessage()
                    return
                  }

                  navigate(
                    AppRoutePaths.setup.provider_affiliations.manage_providers.replace(
                      ':affiliationId',
                      row.id.toString()
                    )
                  )
                }}
              >
                <UserCheckIcon className="text-lg text-gray-400 group-hover:text-primary" />
                <span>Manage Providers</span>
              </MenuItem>

              <MenuItem
                className="group flex justify-start items-center gap-2"
                onClick={() => {
                  if (!canDeleteAffiliation) {
                    void showPermissionDeniedMessage()
                    return
                  }

                  onDeleteAffiliation(row)
                }}
              >
                <TrashCanIcon className="text-lg text-gray-400 group-hover:text-primary" />
                <span>Delete Affiliation</span>
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      )
    },
  }),
]

export default function AffiliationsTable({ affiliations, onRefresh }: Props) {
  const aceTable = useAceTable()
  const navigate = useNavigate()

  const privileges = useUserPrivileges()
  const { showPermissionDeniedMessage } = usePermissionAlert()
  const { toast } = useToast()

  const [selectedStatus, setSelectedStatus] = useState<StatusOption>(
    STATUS_OPTIONS[0]
  )
  const [affiliationToDelete, setAffiliationToDelete] =
    useState<ProviderAffiliation | null>(null)
  const [affiliationToEdit, setAffiliationToEdit] =
    useState<ProviderAffiliation | null>(null)

  const deleteAffiliationMutation = useDeleteProviderAffiliation({
    onSuccess: () => {
      toast({
        title: 'Affiliation Deleted',
        description: 'The affiliation has been deleted successfully.',
        variant: 'default',
      })
      onRefresh()
      setAffiliationToDelete(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const query = aceTable.search.trim().toLowerCase()

  const filteredAffiliations = affiliations.filter((item) => {
    const matchesSearch =
      query.length === 0 ||
      String(item.id).includes(query) ||
      item.name.toLowerCase().includes(query)

    const matchesStatus =
      selectedStatus.name === 'All' || item.status === selectedStatus.name

    return matchesSearch && matchesStatus
  })

  const totalPages = Math.max(
    1,
    Math.ceil(filteredAffiliations.length / aceTable.recordsPerPage)
  )
  const currentPage = Math.min(aceTable.currentPage, totalPages)
  const startIndex = (currentPage - 1) * aceTable.recordsPerPage
  const currentRows = filteredAffiliations.slice(
    startIndex,
    startIndex + aceTable.recordsPerPage
  )

  const columns = createAffiliationsColumns({
    canEditAffiliation: privileges.user.provider_management_edit,
    canManageProviders: privileges.user.provider_management_read,
    canDeleteAffiliation: privileges.user.provider_management_delete,
    navigate,
    showPermissionDeniedMessage,
    onEditAffiliation: (affiliation) => {
      setAffiliationToEdit(affiliation)
    },
    onDeleteAffiliation: (affiliation) => {
      setAffiliationToDelete(affiliation)
    },
  })

  return (
    <div className="w-full h-full">
      <div className="mb-4 flex justify-end">
        <AddAffiliationPopup
          onAddAffiliationSuccess={() => {
            void onRefresh()
          }}
        />
      </div>
      <ACETable<ProviderAffiliation>
        data={currentRows}
        columns={columns}
        totalRows={filteredAffiliations.length}
        currentPage={currentPage}
        recordsPerPage={aceTable.recordsPerPage}
        search={aceTable.search}
        onSearchChange={aceTable.onSearchChange}
        onPageChange={aceTable.onPageChange}
        onPerPageChange={aceTable.onPerPageChange}
        onRefresh={() => {
          void onRefresh()
        }}
        title="Provider Affiliations"
        searchPlaceholder="Search Affiliations"
        filter={
          <SimpleSelector<StatusOption>
            list={STATUS_OPTIONS}
            selected={selectedStatus}
            setSelected={(option) => {
              setSelectedStatus(option)
              aceTable.setCurrentPage(1)
            }}
            className="w-full sm:w-48"
            icon={<FilterIcon className="text-primary" />}
          />
        }
      />
      {affiliationToEdit && (
        <EditAffiliationPopup
          key={affiliationToEdit.id}
          affiliation={affiliationToEdit}
          onOpenChange={(open) => {
            if (!open) {
              setAffiliationToEdit(null)
            }
          }}
          onEditAffiliationSuccess={() => {
            void onRefresh()
          }}
        />
      )}
      <ConfirmDialog
        open={!!affiliationToDelete}
        onOpenChange={(open) => {
          if (!open && !deleteAffiliationMutation.isPending) {
            setAffiliationToDelete(null)
          }
        }}
        title="Delete Affiliation"
        description={
          affiliationToDelete
            ? `Are you sure you want to delete ${affiliationToDelete.name}? This action cannot be undone.`
            : ''
        }
        confirmText="Delete Affiliation"
        cancelText="Cancel"
        variant="destructive"
        confirmDisabled={deleteAffiliationMutation.isPending}
        confirmLoading={deleteAffiliationMutation.isPending}
        confirmLoadingText="Deleting Affiliation..."
        onConfirm={() => {
          if (!affiliationToDelete) {
            return
          }
          deleteAffiliationMutation.mutate(affiliationToDelete.id)
        }}
      />
    </div>
  )
}
